"use client";

import { useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { outlineToggleClass } from "@/lib/quiz-control-styles";
import { useChoicesVisibility } from "@/components/ChoicesVisibilityContext";
import { useEffectivePass } from "@/hooks/useEffectivePass";
import { useArticleShellSlots } from "@/components/article-shell/ArticleShellContext";
import { MobileBottomToolRegistration } from "@/components/mobile-bottom-tools";
import type { MobileBottomToolItem } from "@/components/mobile-bottom-tools";

/**
 * 이론(theory/*)·코딩(coding/*) 글 페이지의 "전체 보기 숨기기" 토글 (JCG-2312).
 *
 * 글 페이지 하단의 연습 문제(ProblemAnswer)는 보기(선택지)가 항상 노출돼 정답이 그대로 보인다.
 * 문제 풀이 화면(RandomProblems/RandomizedProblems)에는 이미 "전체 보기 숨기기" 토글이 있지만
 * 글 페이지엔 연결돼 있지 않았다. 이 컴포넌트가 같은 토글(ChoicesVisibilityContext)을 글 페이지에도
 * 제공한다 — 비주얼·동작은 RandomizedProblems 의 ChoicesToggleBar / RandomProblems 의
 * SidebarFilterControl 과 일치시킨다(공용 outlineToggleClass 재사용).
 *
 * - 프리미엄 이상 전용. 컨텍스트가 없거나(falsy) 프리미엄 미만이면 아무것도 렌더하지 않고,
 *   셸 슬롯 신호(setControlsActive)도 보내지 않는다(기존 토글과 동일 게이트).
 * - 데스크탑 셸: 우측 사이드바 필기 위 quizControlsSlot 에 카드로 portal.
 * - 비셸(모바일/클래식): 하단 Dock 에 "보기" 토글 도구로 등록(JCG-3210). 과거(JCG-2880)엔
 *   ArticleProblemJumpButton 의 `문제` status-panel 시트에 통합돼 있었으나, 상하 단계 이동이
 *   ArticleScrollStepper(우하단 FAB)로 분리되며 보기 숨기기 토글은 다시 이 컴포넌트가 단독 소유한다.
 *
 * 항상 ChoicesVisibilityProvider 안쪽에서 렌더된다(MDXContentWrapper 가 보장) — provider 를 직접
 * 만들지 않는다. 오버레이 렌더는 ProblemAnswer 가 globalChoicesHidden 을 읽어 처리한다.
 */
export default function ArticleChoicesToggle() {
  const ctx = useChoicesVisibility();
  const { isPremiumOrAbove } = useEffectivePass();
  // 셸(데스크탑 글 페이지)이면 컨트롤을 우측 사이드바 필기 위 슬롯으로 portal.
  const { inShell, quizControlsSlot, setControlsActive } = useArticleShellSlots();

  const premiumEnabled = !!ctx && isPremiumOrAbove;
  const choicesHidden = ctx?.globalChoicesHidden ?? false;

  // 모바일/클래식 하단 dock "보기" 토글 (JCG-3210). 훅 규칙상 최상위에서 메모이즈하고, 실제 등록은
  // 아래에서 비셸 + premiumEnabled 일 때만 한다. 눌러 바로 토글하는 action 도구 — 숨김 상태를
  // 아이콘(Eye/EyeOff)·status(숨김)·selected 하이라이트로 표시한다.
  const choicesTool = useMemo<MobileBottomToolItem>(
    () => ({
      id: "article-choices",
      kind: "action",
      label: "보기",
      icon: choicesHidden ? Eye : EyeOff,
      order: 4,
      selected: choicesHidden,
      status: choicesHidden ? "숨김" : undefined,
      ariaLabel: choicesHidden
        ? "전체 보기 숨김 상태, 눌러서 전체 보기 보이기"
        : "전체 보기 숨기기",
      onAction: ctx?.toggleGlobalChoicesHidden,
    }),
    [choicesHidden, ctx],
  );

  // 셸이면 슬롯을 켜둔다(이론 글 페이지는 scoreActive=false 라서 이 신호가 있어야 우측
  // 사이드바 quizControlsSlot 이 렌더된다 — SidebarFilterControl 과 동일 패턴).
  // 프리미엄 미만/컨텍스트 없음이면 신호를 보내지 않는다(게이트 일관성).
  useEffect(() => {
    if (!premiumEnabled || !inShell) return;
    setControlsActive(true);
    return () => setControlsActive(false);
  }, [premiumEnabled, inShell, setControlsActive]);

  // 게이트: 프리미엄 미만 또는 컨텍스트 없음 → 아무것도 렌더하지 않음.
  if (!premiumEnabled) return null;

  // 전체 보기 숨기기 토글 (기존 토글과 동일 마크업).
  const toggleButton = (
    <Button
      onClick={ctx.toggleGlobalChoicesHidden}
      variant="outline"
      size="sm"
      aria-pressed={ctx.globalChoicesHidden}
      className={outlineToggleClass(ctx.globalChoicesHidden)}
    >
      {ctx.globalChoicesHidden ? (
        <>
          <Eye className="h-4 w-4 mr-1" aria-hidden="true" />
          전체 보기 보이기
        </>
      ) : (
        <>
          <EyeOff className="h-4 w-4 mr-1" aria-hidden="true" />
          전체 보기 숨기기
        </>
      )}
    </Button>
  );

  // 셸: 우측 사이드바 필기 위 슬롯에 세로 스택 카드로 portal(좁은 사이드바라 버튼 full-width).
  if (inShell) {
    return quizControlsSlot
      ? createPortal(
          <div className="flex flex-col gap-2 rounded-xl border border-border bg-card p-3 [&_button]:w-full">
            {toggleButton}
          </div>,
          quizControlsSlot,
        )
      : null;
  }

  // 비셸(모바일/클래식): 하단 Dock "보기" 토글 등록(JCG-3210). 셸 카드 portal·setControlsActive 는 위에서 유지.
  return <MobileBottomToolRegistration tool={choicesTool} />;
}
